import type { ComponentPropsWithoutRef, ElementType, ReactNode } from "react";

import React from "react";

import { LoadingIcon } from "../Icons";
import {
  type ButtonSize,
  buttonStyles,
  type ButtonVariant,
} from "./buttonStyles";

export type { ButtonSize, ButtonVariant };

type ButtonBaseProps = {
  size?: ButtonSize;
  variant?: ButtonVariant;
  isLoading?: boolean;
  iconL?: ReactNode;
  iconR?: ReactNode;
  iconClassName?: string;
  internalClassName?: string;
  disabled?: boolean;
};

export type ButtonProps<T extends ElementType = "button"> = ButtonBaseProps & {
  as?: T;
} & Omit<ComponentPropsWithoutRef<T>, keyof ButtonBaseProps | "as">;

export const Button = <T extends ElementType = "button">({
  as,
  disabled = false,
  variant = "brand",
  size = "md",
  className,
  iconClassName,
  internalClassName,
  children,
  isLoading,
  iconL,
  iconR,
  onClick,
  ...otherProps
}: ButtonProps<T>) => {
  const Element: ElementType = as || "button";
  const isButtonElement = Element === "button";
  const isDisabled = disabled || isLoading;

  const handleClick = (event: React.MouseEvent) => {
    if (isDisabled && !isButtonElement) {
      event.preventDefault();
      return;
    }
    (onClick as ((event: React.MouseEvent) => void) | undefined)?.(event);
  };

  return (
    <Element
      className={buttonStyles.container({
        variant,
        size,
        isDisabled,
        isLoading,
        className,
      })}
      onClick={handleClick}
      aria-busy={isLoading || undefined}
      {...(isButtonElement
        ? { disabled: isDisabled }
        : { "aria-disabled": isDisabled || undefined })}
      {...otherProps}
    >
      <div
        className={buttonStyles.internal({
          variant,
          size,
          isDisabled,
          isLoading,
          className: internalClassName,
        })}
      >
        {iconL && (
          <span aria-hidden="true" className={buttonStyles.icon(size)}>
            {iconL}
          </span>
        )}
        {children}
        {iconR && (
          <span aria-hidden="true" className={buttonStyles.icon(size)}>
            {iconR}
          </span>
        )}
      </div>
      {isLoading && (
        <div
          // oxlint-disable-next-line partners-custom-rule/data-testid-format -- legacy id asserted by portal tests; never rename existing ids
          data-testid="button-loading-icon"
          className={buttonStyles.loaderContainer({ className: iconClassName })}
        >
          <LoadingIcon className={buttonStyles.loaderIcon(size)} />
        </div>
      )}
    </Element>
  );
};
